import React, {useReducer, useState} from 'react'
import {Link} from 'react-router-dom'
import qs from 'qs'
import {getImageTitle, sortPhotos} from 'lib/imageUtils'
import {STATES} from 'lib/strings'
import PhotoGallery from 'components/shared/PhotoGallery'
import SelectablePhotoThumb from 'components/shared/SelectablePhotoThumb'
import DownloadZipDropdown from 'components/shared/DownloadZipDropdown'
import Button from 'components/shared/Button'
import './index.scss'

function reducer(state, action) {
  switch (action.type) {
    case 'markPhoto': {
      return {
        ...state,
        selectedPhotos: [...state.selectedPhotos, action.photo]
      }
    }
    case 'unmarkPhoto': {
      return {
        ...state,
        selectedPhotos: state.selectedPhotos.filter(photo => photo.id !== action.photo.id)
      }
    }
    case 'openPhotoPreview': {
      return {
        ...state,
        photoPreviewIndex: action.index,
      }
    }
    case 'closePhotoPreview': {
      return {
        ...state,
        photoPreviewIndex: null,
      }
    }
    case 'resetSelectedPhotos': {
      return {
        ...state,
        selectedPhotos: action.photos,
      }
    }
    default: {
      throw new Error(`Unknown action ${action}`)
    }
  }
}

const DeliveredPhotos = ({
  estateId,
  product,
  photoOrder,
  canCreateCorrections,
  customProfiles = [],
  readOnly = false,
}) => {
  const photoAssets = product.assets.edges
    .map(({approved, node}) => ({...node, selected: approved}))
    .filter(p => p.__typename === 'PhotoAsset')

  const photos = photoOrder ? sortPhotos(photoAssets, photoOrder) : photoAssets
  const selectedPhotosForPurchase = photos.filter(p => p.selected && p.state === 'available_for_purchase')

  const [prevProductId, setPrevProductId] = useState(product.id)
  const [state, stateReducer] = useReducer(reducer, {
    selectedPhotos: selectedPhotosForPurchase,
    photoPreviewIndex: null,
  })

  if (product.id !== prevProductId) {
    stateReducer({type: 'resetSelectedPhotos', photos: selectedPhotosForPurchase})
    setPrevProductId(product.id)
  }


  const photoIsSelected = (photo) => {
    return !!state.selectedPhotos.find(p => p.id === photo.id)
  }

  const additionalCost = product.extraAssetUnitPrice
    ? (state.selectedPhotos.length - product.remainingCredits) * product.extraAssetUnitPrice
    : 0

  const previewPhoto = (index) => stateReducer({type: 'openPhotoPreview', index})
  const closeGallery = () => stateReducer({type: 'closePhotoPreview'})

  const photoSelectedChanged = (photo, selected) => {
    const type = selected ? 'markPhoto' : 'unmarkPhoto'
    stateReducer({type, photo})
  }

  const titleForPhotoIndex = (index) => {
    return getImageTitle(photos[index].image)
  }

  const photoStatusLabel = (photo) => {
    switch (photo.state) {
      case 'purchased':
        return STATES.delivered
      case 'marked_for_correction':
        return STATES.marked_for_correction
      default:
        return null
    }
  }

  const unitPrice = () => {
    if (product.remainingCredits - state.selectedPhotos.length > 0) {
      return null
    }
    return product.extraAssetUnitPrice
  }

  const getPhotoOverlayLabel = (photo) => {
    const statusLabel = photoStatusLabel(photo)
    if (statusLabel) return statusLabel

    const price = unitPrice()
    if (!photoIsSelected(photo) && price > 0) {
      return `Velg (+${price}kr)`
    }
  }

  const additionalCostLabel = additionalCost > 0 ? `(+ ${additionalCost}kr)` : ''

  const galleryCaptionForPhotoIndex = (index) => {
    const photo = photos[index]
    const photoLabel = photoStatusLabel(photo)
    if (photoLabel) return photoLabel

    return (
      <div className="delivered-photos-gallery-caption">
        <span className="total-photos">
          {state.selectedPhotos.length > 0 && (
            <>{state.selectedPhotos.length} bilder valgt {additionalCostLabel}</>
          )}
        </span>
        <span className="toggle-photo">
          <SelectablePhotoThumb.Overlay
            label={getPhotoOverlayLabel(photo)}
            selected={photo.state === 'available_for_purchase' && photoIsSelected(photo)}
            isSelectable={!readOnly && photo.state === 'available_for_purchase'}
            onChange={(selected) => photoSelectedChanged(photo, selected)}
          />
        </span>
      </div>
    )
  }

  const canMarkForCorrection = () => {
    return !readOnly && canCreateCorrections && product.editingComponentIds.length > 0
  }

  const canSwitchProfile = () => {
    return !readOnly && customProfiles.length > 0 && product.editingComponentIds.length > 0
  }

  const toggleSelectedPhotos = (selected) => {
    const selectablePhotos = photos.filter(p => p.state === 'available_for_purchase')
    stateReducer({type: 'resetSelectedPhotos', photos: selected ? selectablePhotos : []})
  }

  const shouldShowAsUnderProduction = window.showAsUnderProduction === product.id
  const anyPhotosToPurchase = photos.some(p => p.state === 'available_for_purchase')
  const anyPhotosPurchased = photos.some(p => p.state === 'purchased') && !shouldShowAsUnderProduction
  const showInProductionNotice = product.state === 'partially_delivered' && photos.length

  return (
    <div className="delivered-photos">
      {showInProductionNotice && (
        <p>Produktet er under produksjon, men allerede leverte bilder er tilgjengelig under. Godkjente bilder kan lastes ned som zip-fil.</p>
      )}

      {!readOnly && anyPhotosToPurchase && (
        <div className="toggle-selected-photos">
          <Button size="small" onClick={toggleSelectedPhotos.bind(null, true)}>Velg alle</Button>
          <Button size="small" onClick={toggleSelectedPhotos.bind(null, false)}>Avvelg alle</Button>
        </div>
      )}

      { !shouldShowAsUnderProduction && 
        <SelectablePhotoThumb.Wrapper>
          {photos.map((photo, i) => (
            <SelectablePhotoThumb
              key={photo.id}
              photo={photo}
              selected={photo.state === 'available_for_purchase' && photoIsSelected(photo)}
              isSelectable={!readOnly && photo.state === 'available_for_purchase'}
              label={getPhotoOverlayLabel(photo)}
              onChange={(selected) => photoSelectedChanged(photo, selected)}
              onClick={() => previewPhoto(i)}
              title={titleForPhotoIndex(i)}
            />
          ))}
        </SelectablePhotoThumb.Wrapper>
      }

      {!readOnly && anyPhotosToPurchase && (
        <div className="purchase-action">
          <Link to={{
            pathname: `/oppdrag/${estateId}/godkjenn-leveranse/${product.id}`,
            search: '?' + qs.stringify({
              assetIds: state.selectedPhotos.map(p => p.id),
            }),
          }}>
            <Button
              type="navigation"
              disabled={state.selectedPhotos.length === 0}
            >
              {state.selectedPhotos.length === 0
                ? <>Du må velge minst ett bilde</>
                : <>Godkjenn {state.selectedPhotos.length} bilder {additionalCostLabel}</>
              }
            </Button>
          </Link>
        </div>
      )}

      <div className="actions">
        {anyPhotosPurchased && (
          <DownloadZipDropdown productId={product.id} />
        )}
        {canSwitchProfile() && (
          <Link to={`/oppdrag/${estateId}/produkt/${product.id}/fotofilter`}>
            <Button secondary type="action">Stilvelger/filter</Button>
          </Link>
        )}
        {canMarkForCorrection() && (
          <Link to={`/oppdrag/${estateId}/produkt/${product.id}/korreksjon`}>
            <Button secondary type="action">Vil du sende bilder til korrigering?</Button>
          </Link>
        )}
      </div>

      {state.photoPreviewIndex !== null && (
        <PhotoGallery
          images={photos.map(p => p.image.url)}
          initialImageIndex={state.photoPreviewIndex}
          onRenderCaption={(i) => galleryCaptionForPhotoIndex(i)}
          onRenderTitle={(i) => titleForPhotoIndex(i)}
          onCloseRequest={closeGallery}
        />
      )}
    </div>
  )
}

export default DeliveredPhotos
